import { useTranslation } from "react-i18next";
import { BsExclamationTriangleFill } from "react-icons/bs";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiPaths } from "../../../api/paths";
import GridDetailItem from "../../../components/grid-detail-item/GridDetailItem";
import useData from "../../../hooks/use-data";
import { ClaimDetailsType } from "../../../types/claim";
import { GridItemType } from "../../../types/common";
import { getStringDate } from "../../../utils/utc-date-conversion";
import "./claim-details.css";

export default function ClaimDetails() {
  const { t } = useTranslation(["common", "myClaims"]);
  const { claimId } = useParams();

  // Fetching data with loading and error states
  const { data, loading, error } = useData<ClaimDetailsType>(
    `${ApiPaths.claims}/${claimId}`,
    {
      onError: (err) => {
        toast(err.response?.data?.message || "An error occurred", {
          type: "error",
        });
      },
    }
  );

  const claim_details_map: GridItemType[] = [
    {
      accessor: "_id",
      label: t("details.label.claim.id", { ns: "myClaims" }),
      value: (val) => {
        return `#${val?._id}`;
      },
    },
    {
      accessor: "claim_amount",
      label: t("details.label.claim.amount", { ns: "myClaims" }),
      value: (val) => `$${val?.claim_amount}`,
    },
  ];

  const client_details_map: GridItemType[] = [
    {
      accessor: "firstName",
      label: t("details.label.client.full_name", { ns: "myClaims" }),
      value: (val) => val?.client_name?.name,
    },
    {
      accessor: "email",
      label: t("details.label.client.email", { ns: "myClaims" }),
      value: (val) => val?.email,
    },
  ];

  const cover_details_map: GridItemType[] = [
    {
      accessor: "_id",
      label: t("details.label.cover.id", { ns: "myCovers" }),
      value: (val) => `#${val?.cover?._id}`,
    },
    {
      accessor: "product_name",
      label: t("details.label.cover.product_name", { ns: "myCovers" }),
      value: (val) => val?.cover?.product?.product_name,
    },
    {
      accessor: "product_type",
      label: t("details.label.cover.product_type", { ns: "myCovers" }),
      value: (val) =>
        val?.cover?.product?.product_type
          ?.map((item: any) => item?.label)
          .join(", "),
    },
    {
      accessor: "cover_amount",
      label: t("details.label.cover.amount", { ns: "myCovers" }),
      value: (val) => (
        <p>
          {new Intl.NumberFormat("en-us", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }).format(val?.cover?.cover_amount)}
        </p>
      ),
    },
    {
      accessor: "cover_premium_amount",
      label: t("details.label.cover.premium", { ns: "myCovers" }),
      value: (val) => (
        <p>
          {new Intl.NumberFormat("en-us", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }).format(val?.cover?.cover_premium_amount)}
        </p>
      ),
    },
    {
      accessor: "cover_start_date",
      label: t("details.label.cover.start_date", { ns: "myCovers" }),
      value: (val) => getStringDate(val?.cover?.cover_start_date),
    },
    {
      accessor: "cover_end_date",
      label: t("details.label.cover.end_date", { ns: "myCovers" }),
      value: (val) => getStringDate(val?.cover?.cover_end_date),
    },
    {
      accessor: "period",
      label: t("details.label.cover.period", { ns: "myCovers" }),
      value: (val) => `${val?.cover?.period} days`,
    },
  ];

  return (
    <div className="p-3 px-md-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="primary-title">
          {t("details.heading.claim", { ns: "myClaims" })}
        </h2>
      </div>

      {/* Loader Spinner */}
      {loading && (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Error message: Show only if there's an error and loading is complete */}
      {!loading && error && (
        <div className="box-shadow px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-5 text-center">
          <BsExclamationTriangleFill className="mb-2 details-error-icon" />
          <h2 className="font-16">
            {t("details.error.fetch_details", { ns: "myClaims" })}
          </h2>
        </div>
      )}

      {/* Display Content Only When Data Is Available */}
      {data && (
        <div className="box-shadow px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-3">
          <div className="">
            <div className="claim-details-status pb-3 mb-3 ">
              <h2 className="primary-title font-14 ">
                {t("details.heading.client", { ns: "myClaims" })}
              </h2>
            </div>
            <div className="client-details-cont d-grid ">
              {client_details_map.map((item) => {
                return (
                  <GridDetailItem
                    key={item.accessor}
                    label={item.label}
                    data={item.value(data)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Render Claim and Cover Details After Data Is Loaded */}
      {data && (
        <>
          <div className="px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-3 box-shadow">
            <div className="claim-details-status d-flex justify-content-between align-items-center pb-3 mb-3 ">
              <h2 className="primary-title font-14">
                {t("details.heading.claim", { ns: "myClaims" })}
              </h2>
              <div>
                <p className="d-none d-md-inline secondary-color font-14 me-1">
                  {t("details.label.claim.status", { ns: "myClaims" })}:
                </p>
                <span
                  className={`${data?.status?.toLowerCase()}-status status-button  py-2 px-3 fw-bold`}
                >
                  {data?.status === "INFO-REQUIRED"
                    ? "ADDITIONAL INFO REQUIRED"
                    : data?.status}
                </span>
              </div>
            </div>
            <div className="claim-details-header  d-grid ">
              {claim_details_map.map((item) => {
                return (
                  <GridDetailItem
                    key={item.accessor}
                    label={item.label}
                    data={item.value(data)}
                  />
                );
              })}
            </div>
          </div>
          <div className="box-shadow px-3 py-3 px-md-5 py-md-4 bg-white rounded-2 mb-5">
            <div className="claim-details-status d-flex justify-content-between pb-3 mb-3 ">
              <h2 className="primary-title font-14 ">
                {t("details.heading.cover", { ns: "myClaims" })}
              </h2>
            </div>
            <div className="client-details client-details-cont d-grid">
              {cover_details_map.map((item) => {
                return (
                  <GridDetailItem
                    key={item.accessor}
                    label={item.label}
                    data={item.value(data)}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
